import { createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  DEFAULT_FINAL_BLOCK,
  FormAudience,
  FormInDTO,
  FormOutDTO,
  isTempElementId,
  withDefaultSmsMessage,
} from '../types';
import { formsAdapter } from './forms.adapter';
import { createFormThunk, deleteFormThunk, fetchFormsThunk, getFormThunk, updateFormThunk } from './forms.thunk';

export type FormBuilderForm = Partial<FormOutDTO>;

interface FormsList extends EntityState<FormInDTO> {
  isLoading: boolean;
  hasError: boolean;
  total: number;
}

export interface FormsState {
  list: FormsList;
  current: {
    isLoading: boolean;
    hasBeenSaved: boolean;
    hasError: boolean;
    form: FormBuilderForm;
  };
  selected: {
    formInfo: FormInDTO | undefined;
    isLoading: boolean;
    hasError: boolean;
  };
}

export const defaultFormBuilderForm: FormBuilderForm = {
  storeIds: [],
  name: '',
  elements: [],
  finalBlock: DEFAULT_FINAL_BLOCK,
  audience: FormAudience.CLIENT,
  pointsCreditEnabled: false,
};

const initialState: FormsState = {
  list: formsAdapter.getInitialState({ isLoading: false, hasError: false, total: 0 }),
  current: {
    isLoading: false,
    hasBeenSaved: false,
    hasError: false,
    form: defaultFormBuilderForm,
  },
  selected: {
    formInfo: undefined,
    isLoading: false,
    hasError: false,
  },
};

export const { actions, reducer } = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    updateCurrentForm: (state, action: PayloadAction<FormBuilderForm>) => {
      state.current.form = { ...state.current.form, ...action.payload };
    },
    setCurrentFormFromSelected: (state, action: PayloadAction<FormInDTO>) => {
      const { storeIds, name, elements, finalBlock, audience, pointsCreditEnabled, reward } = action.payload;
      state.current.form = {
        storeIds,
        name,
        elements,
        finalBlock: withDefaultSmsMessage(finalBlock),
        audience: audience ?? FormAudience.CLIENT,
        pointsCreditEnabled: !!pointsCreditEnabled,
        reward: reward ?? null,
      };
    },
    resetCurrentForm: state => {
      state.current.form = defaultFormBuilderForm;
      state.current.hasBeenSaved = false;
      state.current.hasError = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchFormsThunk.pending, state => {
      state.list.isLoading = true;
      state.list.hasError = false;
    });
    builder.addCase(fetchFormsThunk.fulfilled, (state, action) => {
      formsAdapter.setAll(state.list, action.payload.data);
      state.list.total = action.payload.metadata.total;
      state.list.isLoading = false;
      state.list.hasError = false;
    });
    builder.addCase(fetchFormsThunk.rejected, state => {
      formsAdapter.removeAll(state.list);
      state.list.total = 0;
      state.list.isLoading = false;
      state.list.hasError = true;
    });

    builder.addCase(createFormThunk.pending, state => {
      state.current.isLoading = true;
      state.current.hasError = false;
    });
    builder.addCase(createFormThunk.fulfilled, state => {
      state.current.isLoading = false;
      state.current.hasBeenSaved = true;
      state.current.form = defaultFormBuilderForm;
    });
    builder.addCase(createFormThunk.rejected, state => {
      state.current.isLoading = false;
      state.current.hasError = true;
    });

    builder.addCase(getFormThunk.pending, state => {
      state.selected.isLoading = true;
      state.selected.hasError = false;
      state.selected.formInfo = undefined;
    });
    builder.addCase(getFormThunk.fulfilled, (state, action) => {
      state.selected.formInfo = action.payload;
      state.selected.isLoading = false;
    });
    builder.addCase(getFormThunk.rejected, state => {
      state.selected.isLoading = false;
      state.selected.hasError = true;
    });

    builder.addCase(updateFormThunk.pending, state => {
      state.current.isLoading = true;
      state.current.hasError = false;
    });
    builder.addCase(updateFormThunk.fulfilled, (state, action) => {
      state.selected.formInfo = action.payload;
      if (state.list.entities[action.payload.id]) {
        formsAdapter.upsertOne(state.list, action.payload);
      }
      const sentElements = action.meta.arg.payload.elements;
      if (sentElements) {
        // Only unsaved elements take the ids the server just minted, in order: matching
        // by position would hand a saved question's id, and its answers, to another.
        const sentIds = new Set(sentElements.map(element => element.id).filter(Boolean));
        const mintedIds = action.payload.elements
          .map(element => element.id)
          .filter((id): id is string => !!id && !sentIds.has(id));
        let nextMinted = 0;
        state.current.form.elements = (state.current.form.elements ?? []).map(element =>
          isTempElementId(element.id) && nextMinted < mintedIds.length
            ? { ...element, id: mintedIds[nextMinted++] }
            : element,
        );
      }
      state.current.isLoading = false;
    });
    builder.addCase(updateFormThunk.rejected, state => {
      state.current.isLoading = false;
      state.current.hasError = true;
    });

    builder.addCase(deleteFormThunk.fulfilled, (state, action) => {
      formsAdapter.removeOne(state.list, action.payload);
      state.list.total = Math.max(0, state.list.total - 1);
    });
  },
});

export const { updateCurrentForm, setCurrentFormFromSelected, resetCurrentForm } = actions;
